import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function useCounter(target: number, duration: number, started: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);
  return count;
}

interface StatItem {
  icon: string;
  value: number;
  suffix: string;
  prefix: string;
  label: string;
  sublabel: string;
  color: string;
  glow: string;
  delay: number;
}

const stats: StatItem[] = [
  {
    icon: 'ri-user-star-line',
    value: 50,
    suffix: '+',
    prefix: '',
    label: 'Spokojných klientov',
    sublabel: 'z celého Slovenska a Čiech',
    color: 'from-orange-400 to-amber-400',
    glow: 'bg-orange-500/10',
    delay: 0,
  },
  {
    icon: 'ri-briefcase-4-line',
    value: 80,
    suffix: '+',
    prefix: '',
    label: 'Dokončených projektov',
    sublabel: 'weby, e-shopy, kampane',
    color: 'from-emerald-400 to-teal-400',
    glow: 'bg-emerald-500/10',
    delay: 100,
  },
  {
    icon: 'ri-calendar-check-line',
    value: 15,
    suffix: '+',
    prefix: '',
    label: 'Rokov v IT oblasti',
    sublabel: 'weby, CRM systémy, automatizácie',
    color: 'from-violet-400 to-purple-400',
    glow: 'bg-violet-500/10',
    delay: 200,
  },
  {
    icon: 'ri-line-chart-fill',
    value: 150,
    suffix: '%',
    prefix: '+',
    label: 'Priemerný rast návštevnosti',
    sublabel: 'u klientov po 6 mesiacoch',
    color: 'from-rose-400 to-orange-400',
    glow: 'bg-rose-500/10',
    delay: 300,
  },
];

interface StatCardProps {
  stat: StatItem;
  inView: boolean;
}

function StatCard({ stat, inView }: StatCardProps) {
  const count = useCounter(stat.value, 2000, inView);

  return (
    <div
      className={`group relative flex flex-col items-center text-center transition-all duration-700 cursor-default`}
      style={{ transitionDelay: `${stat.delay}ms` }}
    >
      {/* Glow blob */}
      <div className={`absolute inset-0 ${stat.glow} rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-110`} />

      <div
        className={`relative bg-zinc-900/80 border border-zinc-800/60 group-hover:border-zinc-700/60 rounded-3xl px-8 py-10 w-full transition-all duration-300 group-hover:-translate-y-1 overflow-hidden`}
      >
        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative z-10">
          {/* Icon */}
          <div className={`w-14 h-14 flex items-center justify-center mx-auto mb-6 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10`}>
            <i className={`${stat.icon} text-2xl text-white`} />
          </div>

          {/* Counter */}
          <div className="flex items-end justify-center gap-0.5 mb-3">
            {stat.prefix && (
              <span className={`text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r ${stat.color} mb-1`}>
                {stat.prefix}
              </span>
            )}
            <span
              className={`text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r ${stat.color} tabular-nums leading-none`}
            >
              {count}
            </span>
            {stat.suffix && (
              <span className={`text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r ${stat.color} mb-1`}>
                {stat.suffix}
              </span>
            )}
          </div>

          {/* Label */}
          <p className="text-white font-bold text-lg mb-1.5 leading-tight">{stat.label}</p>
          <p className="text-zinc-500 text-sm leading-snug">{stat.sublabel}</p>
        </div>
      </div>
    </div>
  );
}

export default function StatsSection() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-950 relative overflow-hidden">
      {/* Top separator line */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />
      {/* Bottom separator line */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-orange-500/5 blur-3xl" />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-amber-500/4 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-bar-chart-2-line text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Čísla, ktoré hovoria za nás</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Výsledky,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              nie sľuby
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto leading-relaxed">
            Za každým číslom stojí reálny klient, reálny projekt a reálny rast.
          </p>
        </div>

        {/* Stats grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} inView={inView} />
          ))}
        </div>

        {/* Bottom trust strip */}
        <div
          className={`mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 transition-all duration-700 delay-500 ${inView ? 'opacity-100' : 'opacity-0'}`}
        >
          {[
            { icon: 'ri-map-pin-2-line', text: 'Prešov, Slovensko' },
            { icon: 'ri-customer-service-2-line', text: 'Podpora 7 dní v týždni' },
            { icon: 'ri-user-heart-line', text: 'Priamy kontakt - žiadni prostredníci' },
            { icon: 'ri-lock-line', text: 'GDPR compliant' },
          ].map((badge) => (
            <div key={badge.text} className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors duration-200 cursor-default">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${badge.icon} text-orange-400/80`} />
              </div>
              <span className="text-sm font-medium">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
